'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, Users, Clock, DollarSign } from 'lucide-react';
import Image from 'next/image';
import { useTranslation } from "@/hooks/useTranslation";
import { ensureStringArray, highlightKeywords } from "@/lib/utils";

export default function TypesOfImplantsSection() {
    const { t, isRTL } = useTranslation("all-on-4");
    
    const implantTypes = [
        {
            id: 'all-on-4',
            title: t("types.implantTypes.allOn4.title"),
            description: t("types.implantTypes.allOn4.description"),
            suitability: t("types.implantTypes.allOn4.suitability"),
            pros: ensureStringArray(t("types.implantTypes.allOn4.pros", { returnObjects: true })),
            image: '/images/treatments/implant/dental-implant-vis1.webp'
        },
        {
            id: 'all-on-5',
            title: t("types.implantTypes.allOn5.title"),
            description: t("types.implantTypes.allOn5.description"),
            suitability: t("types.implantTypes.allOn5.suitability"),
            pros: ensureStringArray(t("types.implantTypes.allOn5.pros", { returnObjects: true })),
            image: '/images/treatments/all-on-4/all-on-5-illustration.webp'
        },
        {
            id: 'all-on-6',
            title: t("types.implantTypes.allOn6.title"),
            description: t("types.implantTypes.allOn6.description"),
            suitability: t("types.implantTypes.allOn6.suitability"),
            pros: ensureStringArray(t("types.implantTypes.allOn6.pros", { returnObjects: true })),
            image: '/images/treatments/all-on-4/all-on-6-illustration.webp'
        }
    ];

    const [activeTab, setActiveTab] = useState('all-on-4');

    return (
        <section
            id="types"
            className="py-16 lg:py-24 bg-slate-50 dark:bg-navy-950"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {highlightKeywords(t("types.title"), ["All-On-4", "All-On-5", "All-On-6", "All-On"])}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("types.subtitle")}
                    </p>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-3 mb-12 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                        {(isRTL ? [...implantTypes].reverse() : implantTypes).map((type) => (
                            <TabsTrigger
                                key={type.id}
                                value={type.id}
                                className="data-[state=active]:bg-gold data-[state=active]:text-navy font-medium"
                            >
                                {type.title}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {implantTypes.map((type) => (
                        <TabsContent key={type.id} value={type.id}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Card className="border-slate-200 dark:border-slate-700">
                                    <CardContent className="p-8 lg:p-12">
                                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                                            <div className={`space-y-6 ${isRTL ? 'lg:order-2 text-right' : ''}`}>
                                                <div>
                                                    <h3 className="text-2xl lg:text-3xl font-bold text-navy dark:text-white mb-4">
                                                        {type.title}
                                                    </h3>
                                                    <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                                                        {type.description}
                                                    </p>
                                                </div>

                                                <div className="mb-6">
                                                    <h4 className={`text-lg font-semibold text-navy dark:text-white mb-3 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                        <Users className={`w-5 h-5 text-gold ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                                        {t("types.bestFor")}
                                                    </h4>
                                                    <p className="text-slate-600 dark:text-slate-300">
                                                        {type.suitability}
                                                    </p>
                                                </div>

                                                <div>
                                                    <h4 className="text-lg font-semibold text-navy dark:text-white mb-4">
                                                        {t("types.keyBenefits")}
                                                    </h4>
                                                    <ul className="space-y-3">
                                                        {type.pros.map((pro, index) => (
                                                            <li key={index} className={`flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                                <CheckCircle className={`w-5 h-5 text-gold shrink-0 mt-0.5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                                                                <span className="text-slate-600 dark:text-slate-300">
                                                                    {pro}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                <div className="pt-4">
                                                    <Button
                                                        className="bg-gold hover:bg-gold-600 text-navy font-semibold px-6 py-3"
                                                        onClick={() => {
                                                            const costSection = document.getElementById('cost');
                                                            costSection?.scrollIntoView({ behavior: 'smooth' });
                                                        }}
                                                    >
                                                        {t("types.learnPricing")}
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className={`relative ${isRTL ? 'lg:order-1' : ''}`}>
                                                <div className="relative aspect-square rounded-2xl overflow-hidden bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                                    <Image
                                                        src={type.image}
                                                        alt={`${type.title} illustration`}
                                                        fill
                                                        className="object-cover"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        </section>
    );
} 